import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import { ProjectStateEvent } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import React, { ReactElement, useEffect, useRef } from 'react';

import Menu, { MenuHandler } from '~components/Menu/Menu';

interface ProjectStatusDropdownProps {
  events: ProjectStateEvent[];
  onSelectEvent: (event: ProjectStateEvent) => void;
  pendingEvent?: ProjectStateEvent | null;
  trigger: ReactElement<{
    onClick?: React.MouseEventHandler;
    style?: React.CSSProperties;
  }>;
}

export default function ProjectStatusDropdown({
  events,
  onSelectEvent,
  pendingEvent,
  trigger,
}: ProjectStatusDropdownProps) {
  const theme = useTheme();
  const menuHandler = useRef<MenuHandler>(null);
  const isPendingManaged = pendingEvent !== undefined;
  const isPending = Boolean(pendingEvent);
  const wasPendingRef = useRef(isPending);

  useEffect(() => {
    if (wasPendingRef.current && !isPending) {
      menuHandler.current?.onClose?.();
    }
    wasPendingRef.current = isPending;
  }, [isPending]);

  return (
    <Menu ref={menuHandler} menuTrigger={trigger} preventCloseCondition={isPending}>
      {events.map((event) => (
        <MenuItem
          key={event}
          data-test-id={`project-state-event-${event}`}
          disabled={isPending}
          onClick={(mouseEvent) => {
            mouseEvent.stopPropagation();
            if (isPending) {
              return;
            }
            onSelectEvent(event);
            if (!isPendingManaged) {
              menuHandler.current?.onClose?.();
            }
          }}
        >
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              gap: 1,
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <Typography color={theme.brandV2.colors.treadBlack} sx={{ fontSize: '12px' }}>
              {t(`project.event.${event}`)}
            </Typography>
            <Box
              data-test-id={`project-state-event-${event}-spinner`}
              sx={{
                alignItems: 'center',
                display: pendingEvent === event ? 'flex' : 'none',
                flex: 0,
                justifyContent: 'center',
                minWidth: '15px',
              }}
            >
              <CircularProgress size={15} />
            </Box>
          </Box>
        </MenuItem>
      ))}
    </Menu>
  );
}
